import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";

import { MaterialReactTable } from "material-react-table";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Loader } from "../Utils/Loader";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { ToastContainer } from "react-toastify";
import { useColumnSettings } from "../Utils/useColumnSettings";
import CommonModal from "../Utils/CommonModal";
import ItemsForm from "../../Components/Items/ItemsForm";
import { hasPermission } from "../Utils/UtilityFunctions";
import { getItems } from "../../slices/Item/GetItem/thunk";
import { deleteItem } from "../../slices/Item/DeleteItem/thunk";

function ItemsListings() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const rawPermissions = useSelector(
    (state) => state.permissions.permissionsMap,
  );
  const permissionsMap = new Map(
    rawPermissions.map(([key, value]) => [key, new Set(value)]),
  );
  const itemPermission = hasPermission(permissionsMap, "configuration", "item");
  const viewPermission = hasPermission(permissionsMap, "miscellaneous", "view");

  const [columnFilters, setColumnFilters] = useState([]);
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchText, setSearchText] = useState("");

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteItemData, setDeleteItemData] = useState(null);
  const [messageForPopUp, setMessageForPopUp] = useState("");

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const toggleModal = () => setModalOpen(!modalOpen);

  const { items, loading, error } = useSelector(
    (state) => state.GetItems || {},
  );

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    document.body.style.overflow = "auto";
  };

  const handleDelete = (item) => {
    setDeleteItemData(item);
    setMessageForPopUp(
      `Are you sure you want to delete item "${item?.itemname}" ?`,
    );
    toggleDeleteModal();
  };

  useEffect(() => {
    dispatch(getItems(pagination.pageSize, pagination.pageIndex + 1));
  }, [dispatch, pagination.pageSize, pagination.pageIndex]);

  useEffect(() => {
    if (items?.items) {
      const formattedItems = items.items.map((item) => ({
        ...item,
        item: item.itemname,
        category: item.categorypath?.join(" / "),
      }));

      setAllItems((prev) => {
        const merged = [...prev, ...formattedItems];

        return merged.filter(
          (item, index, self) =>
            index === self.findIndex((x) => x.itemid === item.itemid),
        );
      });

      setTableData(formattedItems);
      setFilteredItems(formattedItems);
    } else {
      setTableData([]);
      setFilteredItems([]);
    }
  }, [items]);

  // CSV EXPORT
  const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
  });

  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(filteredItems);
    download(csvConfig)(csv);
  };

  const handleSuccess = async () => {
    await dispatch(getItems(pagination.pageSize, pagination.pageIndex + 1));

    setModalOpen(false);
  };

  const handleSearch = () => {
    if (!searchText.trim()) {
      setFilteredItems(tableData);
      return;
    }

    const search = searchText.toLowerCase();

    const filtered = allItems.filter((item) => {
      return (
        item?.item?.toLowerCase()?.includes(search) ||
        item?.sku?.toLowerCase()?.includes(search) ||
        item?.category?.toLowerCase()?.includes(search) ||
        item?.brand?.toLowerCase()?.includes(search) ||
        item?.uom?.toLowerCase()?.includes(search)
      );
    });

    setFilteredItems(filtered);
  };

  const handleClear = () => {
    setSearchText("");
    setFilteredItems(tableData);
  };

  const columns = [
    {
      accessorKey: "sku",
      header: "SKU",
    },
    {
      accessorKey: "item",
      header: "Product Name",
    },
    {
      accessorKey: "category",
      header: "Category",
    },
    {
      accessorKey: "brand",
      header: "Brand",
    },
    {
      accessorKey: "uom",
      header: "Unit",
    },
  ];

  const {
    settingsModalOpen,
    viewColumns,
    toggleSettingsModal,
    SettingsSidebar,
    CustomViewModal,
  } = useColumnSettings("items", columns);

  return (
    <React.Fragment>
      <div id="csms">
        <Container fluid>
          <Row>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="rv-page-title m-0">Product List</h5>
            </div>

            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-2 gap-3">
              <div className="d-flex align-items-center w-100 gap-2">
                <Input
                  type="text"
                  placeholder="Type to search here"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  style={{
                    maxWidth: "500px",
                    height: "36px",
                    borderRadius: "4px",
                  }}
                />

                <Button
                  className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap mx-1"
                  onClick={handleSearch}
                >
                  <i className="ri-search-line label-icon align-middle fs-16 me-1 fw-bold"></i>
                  <span className="ps-2 ms-2 me-1 fw-bold">Search</span>
                </Button>

                {(columnFilters.length > 0 || showColumnFilters) && (
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    className="text-nowrap"
                    onClick={() => {
                      setColumnFilters([]);
                      setShowColumnFilters(false);
                    }}
                  >
                    <i className="ri-filter-off-line me-1"></i>
                    Clear Filters
                  </Button>
                )}
              </div>

              {/* ACTIONS */}
              <div className="d-flex align-items-center">
                {itemPermission?.has("export") && (
                  <Button
                    className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap mx-1"
                    onClick={handleExportData}
                  >
                    <i className="ri-download-line label-icon secondary-label-icon align-middle fs-16 me-2 fw-bold"></i>
                    <span className="px-2 ps-3 ms-2 me-1 fw-bold">Export</span>
                  </Button>
                )}

                {itemPermission?.has("create") && (
                  <Button
                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap mx-1"
                    onClick={() => {
                      setSelectedItem(null);

                      toggleModal();
                    }}
                  >
                    <i className="ri-add-fill label-icon align-middle fs-16 me-1 fw-bold"></i>
                    <span className="ps-2 ms-2 me-1 fw-bold">Add Product</span>
                  </Button>
                )}
                {/* {itemPermission && itemPermission?.has("view") && (
                  <div
                    className="btn-icon cursor-img"
                    onClick={toggleSettingsModal}
                  >
                    <i className="ri-settings-2-line fs-4"></i>
                  </div>
                )} */}
              </div>
            </div>

            <Col lg={12}>
              <div
                className="mb-3 mt-1 border table-responsive"
                style={{ borderRadius: "4px" }}
              >
                {loading && <Loader />}

                <MaterialReactTable
                  columns={columns.filter((col) =>
                    viewColumns?.includes(col.accessorKey || col.header),
                  )}
                  data={filteredItems}
                  enableRowActions
                  enableColumnOrdering={false}
                  enableColumnDragging={false}
                  enableDensityToggle={false}
                  enableColumnActions
                  enableTopToolbar={false}
                  manualPagination
                  rowCount={items?.totalcount || 0}
                  onPaginationChange={setPagination}
                  state={{ pagination, columnFilters, showColumnFilters }}
                  onColumnFiltersChange={(filters) => {
                    setColumnFilters(filters);
                    if (filters.length === 0) setShowColumnFilters(false);
                  }}
                  onShowColumnFiltersChange={setShowColumnFilters}
                  displayColumnDefOptions={{
                    "mrt-row-actions": {
                      header: "",
                      size: 20,
                      minSize: 20,
                      maxSize: 20,
                    },
                  }}
                  muiTableBodyCellProps={({ column }) => ({
                    sx:
                      column.id === "mrt-row-actions"
                        ? {
                            padding: "0 0px",
                          }
                        : {},
                  })}
                  renderRowActionMenuItems={({ row, closeMenu }) =>
                    [
                      itemPermission?.has("update") && (
                        <MenuItem
                          key="edit"
                          onClick={() => {
                            closeMenu();
                            setSelectedItem(row.original);
                            setModalOpen(true);
                          }}
                        >
                          <i className="bx bxs-edit me-2" />
                          Edit
                        </MenuItem>
                      ),

                      itemPermission?.has("delete") && (
                        <MenuItem
                          key="delete"
                          onClick={() => {
                            closeMenu();
                            handleDelete(row.original);
                          }}
                        >
                          <i className="ri ri-delete-bin-5-line me-2" />
                          Delete
                        </MenuItem>
                      ),
                    ].filter(Boolean)
                  }
                />
              </div>
            </Col>

            {CustomViewModal()}
          </Row>

          {/* COMMON MODAL */}
          <CommonModal
            modalHeader="Delete Product"
            open={deleteModal}
            toggle={toggleDeleteModal}
            buttonText="Yes, Delete It"
            closeButtonText="Cancel"
            messageForPopUp={messageForPopUp}
            modalAction={async () => {
              await dispatch(
                deleteItem(deleteItemData?.itemid, setDeleteLoading),
              );

              await dispatch(
                getItems(pagination.pageSize, pagination.pageIndex + 1),
              );

              toggleDeleteModal();
            }}
          />
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md">
        <ModalHeader
          toggle={toggleModal}
          style={{
            borderBottom: "1px solid #FD7545",
            padding: "16px 20px",
          }}
        >
          <span
            style={{
              color: "#FD7545",
              fontWeight: "600",
            }}
          >
            {selectedItem ? "Edit Product" : "Add New Product"}
          </span>
        </ModalHeader>

        <ModalBody
          className="overflow-auto"
          style={{ maxHeight: "70vh", padding: "20px 20px 0px 20px" }}
        >
          <ItemsForm
            closeModal={toggleModal}
            onSuccess={handleSuccess}
            selectedItem={selectedItem}
          />
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
}

export default ItemsListings;
